import React, { useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import { useNavigate , useParams} from 'react-router-dom';
import { IoChevronForward, IoChevronBack } from 'react-icons/io5';
import { FiSettings } from 'react-icons/fi';
import { PiBooksFill } from 'react-icons/pi';
import { BsBook } from 'react-icons/bs';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { BiTime } from 'react-icons/bi';
import MeetingItem from '../components/MeetingItem';
import { useNoobookContext } from '../context/NoobookContext';
import { useUserInfoQuery } from '../react-queries/queries';
import { noobookRole2text, ActivityType, RecordType } from '../common/enums';
import { getMyBookcommunities } from '../api/BookcommunityApi';
import { getMyBookUserStatistics } from '../api/BookcaseApi';
import { processApiResponse } from '../common/errorMessage';
import { getAccessToken } from '../react-queries/queries';
import LoadingOverlay from '../components/LoadingOverlay';
import MoimDetailPage from './MoimDetailPage';
import MembershipSubscriptionPage from './MembershipSubscriptionPage';
import { FaTimes } from 'react-icons/fa';

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #eae8e9;
  min-height: 100vh;
`;

const Content = styled.div`
  padding: 0 0 80px 0;
`;

const HeaderSection = styled.div`
  background: #eae8e9;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
`;

const ProfileArea = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const ProfileImageWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #eaeaea;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 24px;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const Username = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #212529;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserId = styled.div`
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 8px;
`;

const PremiumBadge = styled.span`
  background: #e64980;
  color: white;
  font-size: 13px;
  border-radius: 4px;
  padding: 2px 4px;
  white-space: nowrap;
  display: inline-block;
  font-weight: 400;
`;

const SettingsButton = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const BenefitsSection = styled.div`
  position: relative;
  width: 100%;
  height: 100px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const BenefitsBanner = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BenefitsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.95) 0%, rgba(0, 86, 179, 0.95) 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  color: white;
`;

const BenefitsContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const BenefitsTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 6px;
`;

const BenefitsList = styled.div`
  font-size: 12px;
  line-height: 1.3;
  margin-bottom: 10px;
`;

const JoinButton = styled.button`
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TabRow = styled.div`
  display: flex;
  background: #eae8e9;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button`
  flex: 1;
  background: none;
  border: none;
  padding: 16px;
  font-size: 16px;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  color: ${props => props.active ? '#212529' : '#6c757d'};
  border-bottom: ${props => props.active ? '2px solid #212529' : 'none'};
  cursor: pointer;
`;

const NameCardTab = styled.button`
  background: ${props => props.active ? '#212529' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 2px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin: 8px 20px 8px 12px;
  flex-shrink: 0;
  height: 24px;
  line-height: 1;
  align-self: center;
  
  &:hover {
    background: ${props => props.active ? '#212529' : '#e9ecef'};
  }
`;

const SubTabRow = styled.div`
  display: flex;
  background: #eae8e9;
  padding: 12px 20px;
  gap: 8px;
  border-bottom: 1px solid #e9ecef;
`;

const SubTab = styled.button`
  background: ${props => props.active ? '#222' : '#bdbdbd'};
  color: ${props => props.active ? '#fff' : '#fff'};
  border: none;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
`;

const StatisticsSection = styled.div`
  background: #eae8e9;
  margin-top: 12px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: transparent;
  margin: 0 20px 6px 20px;
  border: none;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  margin-right: 16px;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 18px;
  font-weight: normal;
  color: #212529;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const StatLink = styled.button`
  background: none;
  border: none;
  color: #6c757d;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: #495057;
  }
`;

const MeetingSection = styled.div`
  background: #eae8e9;
  padding: 20px 0;
`;

const MeetingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 14px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: #fff;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
`;

const ModalTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #222;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;


const MembershipModal = ({ onClose }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>멤버십 구독</ModalTitle>
        <ModalCloseButton onClick={()=>{onClose();}}>
          <FaTimes />
        </ModalCloseButton>
      </ModalHeader>
      <ModalContent>
        <MembershipSubscriptionPage onClose={onClose} />
      </ModalContent>
    </Modal>
  );
};

const MoimDetailModal = ({bookCommunityId, onClose, onModified }) => {
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>상세 보기</ModalTitle>
        <ModalCloseButton onClick={()=>{onClose();}}>
          <FaTimes />
        </ModalCloseButton>
      </ModalHeader>
      <ModalContent>
        <MoimDetailPage bookCommunityId={bookCommunityId} onModified={onModified} />
      </ModalContent>
    </Modal>
  )
};


export default function MyInfoPage() {
  const { activeTab: parmActiveTab } = useParams();
  const navigate = useNavigate();
  const { showBack, setShowBack, logoText, setLogoText } = useNoobookContext();
  const [activeTab, setActiveTab] = useState(parmActiveTab || 'records');
  const [activeSubTab, setActiveSubTab] = useState('noodok');
  const [bookcommunities, setBookcommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({
    doneCnt: 0,
    inProgressCnt: 0,
    memoCnt: 0,
    totalTakenTime: 0
  });
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [showMoimDetailModal, setShowMoimDetailModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selBookCommunityId, setSelBookCommunityId] = useState(null);
  const [isMoimDetailModified, setIsMoimDetailModified] = useState(false);
  const {data: userInfo} = useUserInfoQuery();

  useEffect(() => {
    var prevShowBack = showBack;
    var prevLogoText = logoText;
    setShowBack(true);
    setLogoText('내정보');
    
    return () => {
      setShowBack(prevShowBack);
      setLogoText(prevLogoText);
    };
  }, [setShowBack, setLogoText]);

  const loadMyBookcommunities = useCallback(async () => {
    const {accessToken} = await getAccessToken();

    setLoading(true);
    try {
      const activityType = activeSubTab === 'noodok' 
        ? ActivityType.BOOK_REVIEW 
        : ActivityType.BOOK_SCRIPT;
      
      const res = await getMyBookcommunities(accessToken, activityType);
      const processedResponse = processApiResponse(res);
      
      if (processedResponse.success) {
        setBookcommunities(processedResponse.data || []);
      } else {
        console.error('내 북커뮤니티 검색 중 오류 발생:', processedResponse.errorMessage);
        setBookcommunities([]);
      }
    } catch (error) {
      console.error('내 북커뮤니티 검색 중 오류 발생:', error);
      setBookcommunities([]);
    } finally {
      setLoading(false);
    }
  }, [activeSubTab]);

  // 북커뮤니티 목록 로드
  useEffect(() => {
    if (activeTab === 'meetings') {
      loadMyBookcommunities();
    }
  }, [activeTab, activeSubTab, loadMyBookcommunities]);

  // 통계 데이터 로드
  const loadStatistics = useCallback(async () => {
   
    const {accessToken} = await getAccessToken();

    setStatisticsLoading(true);
    try {
      const recordType = activeSubTab === 'noodok' 
        ? RecordType.READING 
        : RecordType.SCRIPTING;
      
      const res = await getMyBookUserStatistics(accessToken, recordType);
      const processedResponse = processApiResponse(res);
      
      if (processedResponse.success && processedResponse.data) {
        setStatistics({
          doneCnt: processedResponse.data.doneCnt || 0,
          inProgressCnt: processedResponse.data.inProgressCnt || 0,
          memoCnt: processedResponse.data.memoCnt || 0,
          totalTakenTime: processedResponse.data.totalTakenTime || 0
        });
      } else {
        console.error('통계 데이터 조회 중 오류 발생:', processedResponse.errorMessage);
        setStatistics({
          doneCnt: 0,
          inProgressCnt: 0,
          memoCnt: 0,
          totalTakenTime: 0
        });
      }
    } catch (error) {
      console.error('통계 데이터 조회 중 오류 발생:', error);
      setStatistics({
        doneCnt: 0,
        inProgressCnt: 0,
        memoCnt: 0,
        totalTakenTime: 0
      });
    } finally {
      setStatisticsLoading(false);
    }
  }, [activeSubTab]);

  useEffect(() => {
    if (activeTab === 'records') {
      loadStatistics();
    }
  }, [activeTab, activeSubTab, loadStatistics]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
   // if (onTabChange) {
   //   onTabChange(tab);
   // }
  };

  const handleSubTabChange = (subTab) => {
    setActiveSubTab(subTab);
  };

  //const handleBackClick = () => {
  //  navigate(-1);
  //};

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleJoinMembership = () => {
    setShowMembershipModal(true);
  };

  const handleMembershipClose = () => {
    setShowMembershipModal(false);
  };

  const handleViewDetails = (type) => {
    // 상세 기록 보기
    console.log(`${type} 상세 기록 보기`);
  };

  const handleMeetingClick = (bookcommunity) => {
    popupMoimDetail(bookcommunity?.id);
    
  };

  const popupMoimDetail = (bookCommunityId) => {
    console.log('popupMoimDetail', bookCommunityId);
    setSelBookCommunityId(bookCommunityId);
    setShowMoimDetailModal(true);
  };

  const handleMoimDetailClose = (modified) => {
    setShowMoimDetailModal(false);
    if(modified) {
      loadMyBookcommunities();
      setIsMoimDetailModified(false);
    }
  };

  const handleMoimDetailModified = (modified) => {
    //setShowMoimDetailModal(false);
    if(modified) {
      //loadMyBookcommunities();
      setIsMoimDetailModified(true);
    }
  };

  // 뒤로가기 버튼으로 모달 닫기
  useEffect(() => {
    const handlePopState = (event) => {
      if (showMembershipModal) {
        event.preventDefault();
        handleMembershipClose();
      } else if (showMoimDetailModal) {
        event.preventDefault();
        handleMoimDetailClose(false);
        // history를 다시 push하여 뒤로가기 이벤트를 취소
        //window.history.pushState(null, '', window.location.href);
      }
    };

    // 모달이 열려있을 때만 history state를 push하고 이벤트 리스너 등록
    if (showMoimDetailModal || showMembershipModal) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showMoimDetailModal, showMembershipModal]);


  // 초를 시간과 분으로 변환
  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return { hours: 0, minutes: 0 };
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return { hours, minutes };
  };

  //console.log(userInfo);

  return (
    <Wrapper>
      <Content>
        <HeaderSection>
          <ProfileArea>
            <ProfileImageWrapper>
              {userInfo?.imgFileUrl ? (
                <ProfileImage src={userInfo.imgFileUrl} alt="프로필" />
              ) : (
                <ProfileImagePlaceholder>
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="16" fill="#e0e0e0"/>
                    <path d="M16 16c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-3.33 0-10 1.667-10 5v2h20v-2c0-3.333-6.67-5-10-5z" fill="#bbb"/>
                  </svg>
                </ProfileImagePlaceholder>
              )}
            </ProfileImageWrapper>
            <ProfileInfo>
              <Username>
                {userInfo?.name??userInfo?.loginid}님
                <PremiumBadge>{noobookRole2text(userInfo?.userRole)}</PremiumBadge>
              </Username>
              <UserId>{userInfo?.loginid}</UserId>
            </ProfileInfo>
            <SettingsButton onClick={handleSettingsClick}>
              <FiSettings size={20} />
            </SettingsButton>
          </ProfileArea>

          <BenefitsSection>
            <BenefitsBanner src="/banner_sample.jpg" alt="혜택 배너" />
            <BenefitsOverlay>
              <BenefitsContent>
                <BenefitsTitle>누북 멤버로 다양한 혜택을 누리세요.</BenefitsTitle>
                <BenefitsList>
                  서평단 ㆍ문구 체험단 지원<br />
                  공간대여 할인, 모임 회원 모집 자격
                </BenefitsList>
              </BenefitsContent>
              <JoinButton onClick={handleJoinMembership}>
                정식 멤버 가입하기
              </JoinButton>
            </BenefitsOverlay>
          </BenefitsSection>
        </HeaderSection>

        <TabRow>
          <Tab 
            active={activeTab === 'records'} 
            onClick={() => handleTabChange('records')}
          >
            나의기록
          </Tab>
          <Tab 
            active={activeTab === 'meetings'} 
            onClick={() => handleTabChange('meetings')}
          >
            나의모임
          </Tab>
          <NameCardTab 
            active={false} 
            onClick={() => navigate('/namecard')}
          >
            네임카드
          </NameCardTab>
        </TabRow>

        {activeTab === 'records' && (
          <>
            <SubTabRow>
              <SubTab 
                active={activeSubTab === 'noodok'} 
                onClick={() => handleSubTabChange('noodok')}
              >
                누독
              </SubTab>
              <SubTab 
                active={activeSubTab === 'nupil'} 
                onClick={() => handleSubTabChange('nupil')}
              >
                누필
              </SubTab>
            </SubTabRow>

            <StatisticsSection>
              {statisticsLoading ? (
                <EmptyMessage>로딩 중...</EmptyMessage>
              ) : (
                <>
                  <StatItem>
                    <StatIcon>
                      <PiBooksFill size={22} />
                    </StatIcon>
                    <StatContent>
                      <StatValue><span style={{fontWeight: 'bold'}}>{statistics.doneCnt}</span>권 {activeSubTab === 'nupil' ? '완필' : '완독'}</StatValue>
                    </StatContent>
                   {/* <StatLink onClick={() => handleViewDetails(activeSubTab === 'nupil' ? '완필' : '완독')}>
                      상세기록보기
                      <IoChevronForward size={12} />
                    </StatLink>*/}
                  </StatItem>

                  <StatItem>
                    <StatIcon>
                      <BsBook size={22} />
                    </StatIcon>
                    <StatContent>
                      <StatValue><span style={{fontWeight: 'bold'}}>{statistics.inProgressCnt}</span>권 {activeSubTab === 'nupil' ? '필사 중' : '읽는 중'}</StatValue>
                    </StatContent>
                    {/*<StatLink onClick={() => handleViewDetails(activeSubTab === 'nupil' ? '필사중' : '읽는중')}>
                      상세기록보기
                      <IoChevronForward size={12} />
                    </StatLink>*/}
                  </StatItem>

                  <StatItem>
                    <StatIcon>
                      <PencilSquareIcon style={{width: 22, height: 22}} />
                    </StatIcon>
                    <StatContent>
                      <StatValue><span style={{fontWeight: 'bold'}}>{statistics.memoCnt}</span>장 메모</StatValue>
                    </StatContent>
                    {/*<StatLink onClick={() => handleViewDetails('메모')}>
                      상세기록보기
                      <IoChevronForward size={12} />
                    </StatLink>*/}
                  </StatItem>

                  <StatItem>
                    <StatIcon>
                      <BiTime size={22} />
                    </StatIcon>
                    <StatContent>
                      <StatValue>
                        <span style={{fontWeight: 'bold'}}>{formatTime(statistics.totalTakenTime).hours}</span>시간{' '}
                        <span style={{fontWeight: 'bold'}}>{formatTime(statistics.totalTakenTime).minutes}</span>분
                      </StatValue>
                      <StatLabel>{activeSubTab === 'nupil' ? '총 필사시간' : '총 독서시간'}</StatLabel>
                    </StatContent>
                  </StatItem>
                </>
              )}
            </StatisticsSection>
          </>
        )}

        {activeTab === 'meetings' && (
          <>
            <SubTabRow>
              <SubTab 
                active={activeSubTab === 'noodok'} 
                onClick={() => handleSubTabChange('noodok')}
              >
                누독
              </SubTab>
              <SubTab 
                active={activeSubTab === 'nupil'} 
                onClick={() => handleSubTabChange('nupil')}
              >
                누필
              </SubTab>
            </SubTabRow>

            <MeetingSection>
              {loading ? (
                <EmptyMessage>로딩 중...</EmptyMessage>
              ) : bookcommunities.length === 0 ? (
                <EmptyMessage>참여 중인 모임이 없습니다.</EmptyMessage>
              ) : (
                <MeetingList>
                  {bookcommunities.map((bookcommunity, index) => (
                    <MeetingItem
                      key={bookcommunity.id}
                      image={bookcommunity.imgUrl || (activeSubTab === 'noodok' ? '/moim_sample.png' : '/nupil_sample.png')}
                      title={bookcommunity.title || ''}
                      tag={activeSubTab === 'noodok' ? '#서평' : '#필사'}
                      description={bookcommunity.briefIntroduction || ''}
                      hashtags={bookcommunity.hashtags ? bookcommunity.hashtags.map(tag => `#${tag}`).join(' ') : ''}
                      onClick={() => handleMeetingClick(bookcommunity)}
                    />
                  ))}
                </MeetingList>
              )}
            </MeetingSection>
          </>
        )}      
      </Content>
      {showMoimDetailModal && (
        <MoimDetailModal bookCommunityId={selBookCommunityId} onClose={()=>handleMoimDetailClose(isMoimDetailModified)} onModified={handleMoimDetailModified} />
      )}
      {showMembershipModal && (
        <MembershipModal onClose={handleMembershipClose} />
      )}
      {loading && <LoadingOverlay />}
      {statisticsLoading && <LoadingOverlay />}
    </Wrapper>
  );
} 